import React, { useEffect, useState } from 'react';
import { Form, Row, Col, Button, Input, Spin, Table, Switch, Popconfirm, Modal, message } from 'antd';
import { QRCodeSVG } from 'qrcode.react';

import { BASE_URL } from '../../utils/constants'
import Layout from '../../components/Layout';
import { showErrorModal } from '../../components/Modal';
import api from '../../api/api';
import { LinkProfile } from '../../models'
import { useLinkProfile } from '../../api/useLinkProfile';

const LinksManager: React.FC = () => {
  const [newLinkProfile, setNewLinkProfile] = useState<Partial<LinkProfile>>({});
  const [editingProfile, setEditingProfile] = useState<LinkProfile | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  const { loading, linkProfiles, getLinkProfiles } = useLinkProfile()

  useEffect(() => {
    fetchLinkProfiles();
  }, []);

  const fetchLinkProfiles = async () => {
    getLinkProfiles().catch( (err) => {
      console.error('Failed to load links', err)
      message.error('Failed to load links')
    })
  }

  const handleAddLinkProfile = async (e: React.FormEvent) => {
    // e.preventDefault();

    try {
      await api.post('/v1/link_profile/', newLinkProfile);
      setNewLinkProfile({});
      fetchLinkProfiles();
      message.success('Link profile added successfully');
    } catch (err) {
      console.error('Failed to add link profile', err);
      showErrorModal('Failed to add link profile')
    }
  };

  const handleToggleActive = async (id: number, active: boolean) => {
    try {
      await api.patch(`/v1/link_profile/${id}/`, { active: !active });
      fetchLinkProfiles();
      message.success(`Link ${active ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      console.error('Failed to update link', err);
      message.error('Failed to update link status');
    }
  };

  const handleDeleteLink = async (id: number) => {
    try {
      await api.delete(`/v1/link_profile/${id}/`);
      fetchLinkProfiles();
      message.success('Link profile deleted successfully');
    } catch (err) {
      console.error('Failed to delete link', err);
      showErrorModal('Failed to delete link profile');
    }
  };

  const handleEditProfile = async () => {
    try {
      if (!editingProfile) return;

      await api.put(`/v1/link_profile/${editingProfile.id}/`, editingProfile);
      setIsEditModalVisible(false);
      setEditingProfile(null);
      fetchLinkProfiles();
      message.success('Link profile updated successfully');
    } catch (err) {
      console.error('Failed to update link', err);
      message.error('Failed to update link');
      showErrorModal('Failed to update link profile');
    }
  };

  const showQrCode = (url: string) => {
    setQrCodeUrl(url);
    setQrModalVisible(true);
  };

  const handleDuplicateProfile = async (profile: LinkProfile) => {
    if (!profile) {
      message.error('profile are required');
      return;
    }
    const { id, user_profile, ...profileCopy } = profile;
    const maxId = linkProfiles ? Math.max(...linkProfiles.map(p => p.id)) : 0;

    profileCopy.name = `${profileCopy.name} (${maxId + 1})`;
    profileCopy.path = `${profileCopy.path}-${maxId + 1}`;

    await api.post('/v1/link_profile/', profileCopy).then(() => {
      fetchLinkProfiles();
      message.success('Link profile duplicated successfully');
    }).catch((err) => {
      console.error('Failed to duplicate link', err);
      showErrorModal('Failed to duplicate link profile');
    })
  };

  const columns = [
    // {
    //   title: 'Name',
    //   dataIndex: 'name',
    //   key: 'name',
    // },
    // {
    //   title: 'Clicks',
    //   dataIndex: 'link_counter',
    //   key: 'link_counter',
    // },
    {
      title: 'Path',
      dataIndex: 'path',
      key: 'path',
      render: (path: string) => (path === '' ? '(Main link)' : `/${path}`)
    },
    {
      title: 'Full Link',
      dataIndex: 'path',
      key: 'id',
      render: (path: string, item: LinkProfile) => {
        let url = `${BASE_URL}/${item.user_profile.username}`
        if (path) {
          url += `/${path}`
        }

        return (
          <div className="flex items-center justify-between gap-2 max-w-[400px]">
            <a href={url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline truncate">
              {url}
            </a>
            <Button
              onClick={(e) => {
                e.preventDefault();
                showQrCode(url);
              }}
            >
              Gen QR Code
            </Button>
          </div>
        )
      }
    },
    {
      title: 'URL',
      dataIndex: 'video_link',
      key: 'video_link',
      render: (url: string, item: LinkProfile) => (
        <div className="max-w-[300px]">
          <div className="truncate">
            Video URL:
            <a href={url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
              {' ' + url}
            </a>
          </div>
          <div className="truncate">
            About URL:
            <a href={item.guide_link} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
              {' ' + item.guide_link}
            </a>
          </div>
          <div className="truncate">
            Guide URL:
            <a href={item.about_link} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
              {' ' + item.about_link}
            </a>
          </div>
        </div>
      ),
    },
    {
      title: 'Enabled',
      key: 'active',
      render: (record: LinkProfile) => (
        <Switch
          checked={record.active}
          onChange={() => handleToggleActive(record.id, record.active)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: LinkProfile) => (
        <>
          <Button type="link"
            onClick={() => {
              handleDuplicateProfile(record)

            }}>
            Duplicate
          </Button>

          <Button type="link"
            onClick={() => {
              setEditingProfile(record);
              setIsEditModalVisible(true);
            }}>
            Edit
          </Button>

          <Popconfirm
            title="Are you sure you want to delete this link?"
            onConfirm={() => handleDeleteLink(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">Manage Links</h1>

          <Form
            onFinish={handleAddLinkProfile}
            layout="vertical"
            className="mb-10"
          >
            <Row gutter={[24, 16]}>
              <Col span={12}>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, message: 'Please input name!' }]}
                >
                  <Input
                    value={newLinkProfile.name}
                    onChange={(e) => setNewLinkProfile({ ...newLinkProfile, name: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[{ required: false, message: 'Please input title!' }]}
                >
                  <Input
                    value={newLinkProfile.title}
                    onChange={(e) => setNewLinkProfile({ ...newLinkProfile, title: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Path"
                  name="path"
                  rules={[{ required: false, message: 'Please input path!' }]}
                >
                  <Input
                    value={newLinkProfile.path}
                    onChange={(e) => setNewLinkProfile({ ...newLinkProfile, path: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Video URL"
                  name="video_url"
                  rules={[{ required: true, message: 'Please input video URL!' }]}
                >
                  <Input
                    value={newLinkProfile.video_link}
                    onChange={(e) => setNewLinkProfile({ ...newLinkProfile, video_link: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Guide Text"
                  name="guide_text"
                  rules={[{ required: true, message: 'Please input guide text!' }]}
                >
                  <Input
                    value={newLinkProfile.guide_text}
                    onChange={(e) => setNewLinkProfile({ ...newLinkProfile, guide_text: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Guide URL"
                  name="guide_url"
                  rules={[{ required: true, message: 'Please input guide URL!' }]}
                >
                  <Input
                    value={newLinkProfile.guide_link}
                    onChange={(e) => setNewLinkProfile({ ...newLinkProfile, guide_link: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="About Text"
                  name="about_text"
                  rules={[{ required: true, message: 'Please input about text!' }]}
                >
                  <Input
                    value={newLinkProfile.about_text}
                    onChange={(e) => setNewLinkProfile({ ...newLinkProfile, about_text: e.target.value })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="About URL"
                  name="about_url"
                  rules={[{ required: true, message: 'Please input about URL!' }]}
                >
                  <Input
                    value={newLinkProfile.about_link}
                    onChange={(e) => setNewLinkProfile({ ...newLinkProfile, about_link: e.target.value })}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Add Link
              </Button>
            </Form.Item>
          </Form>

          {loading ? (
            <div className="flex justify-center">
              <Spin size="large" />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={linkProfiles}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
              style={{ maxWidth: '100%' }}
            />
          )}
        </div>
      </div>

      <Modal
        title="Edit Link Profile"
        open={isEditModalVisible}
        onOk={handleEditProfile}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingProfile(null);
        }}
        width={800}
      >
        {editingProfile && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-6">
            <div>
              <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <Input
                id="edit-name"
                value={editingProfile.name}
                onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="edit-title" className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <Input
                id="edit-title"
                value={editingProfile.title}
                onChange={(e) => setEditingProfile({ ...editingProfile, title: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="edit-path" className="block text-sm font-medium text-gray-700 mb-2">
                Path
              </label>
              <Input
                id="edit-path"
                value={editingProfile.path}
                onChange={(e) => setEditingProfile({ ...editingProfile, path: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="edit-video-url" className="block text-sm font-medium text-gray-700 mb-2">
                Video URL
              </label>
              <Input
                id="edit-video-url"
                value={editingProfile.video_link}
                onChange={(e) => setEditingProfile({ ...editingProfile, video_link: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="edit-guide-text" className="block text-sm font-medium text-gray-700 mb-2">
                Guide Text
              </label>
              <Input
                id="edit-guide-text"
                value={editingProfile.guide_text}
                onChange={(e) => setEditingProfile({ ...editingProfile, guide_text: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="edit-guide-url" className="block text-sm font-medium text-gray-700 mb-2">
                Guide URL
              </label>
              <Input
                id="edit-guide-url"
                value={editingProfile.guide_link}
                onChange={(e) => setEditingProfile({ ...editingProfile, guide_link: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="edit-about-text" className="block text-sm font-medium text-gray-700 mb-2">
                About Text
              </label>
              <Input
                id="edit-about-text"
                value={editingProfile.about_text}
                onChange={(e) => setEditingProfile({ ...editingProfile, about_text: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="edit-about-url" className="block text-sm font-medium text-gray-700 mb-2">
                About URL
              </label>
              <Input
                id="edit-about-url"
                value={editingProfile.about_link}
                onChange={(e) => setEditingProfile({ ...editingProfile, about_link: e.target.value })}
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="QR Code"
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={[
          <Button
            key="download"
            type="primary"
            onClick={() => {
              const svg = document.querySelector('.qr-code-svg');
              if (svg) {
                const svgData = new XMLSerializer().serializeToString(svg);
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();
                img.onload = () => {
                  canvas.width = img.width;
                  canvas.height = img.height;
                  ctx?.drawImage(img, 0, 0);
                  const pngFile = canvas.toDataURL('image/png');
                  const downloadLink = document.createElement('a');
                  downloadLink.download = `qr-code.png`;
                  downloadLink.href = pngFile;
                  downloadLink.click();
                };
                img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
              }
            }}
          >
            Download QR Code
          </Button>,
          <Button key="close" onClick={() => setQrModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        <div className="flex justify-center p-4">
          <QRCodeSVG
            value={qrCodeUrl}
            size={256}
            className="qr-code-svg"
            includeMargin={true}
          />
        </div>
        <div className="text-center mt-4">
          <p className="text-gray-600">{qrCodeUrl}</p>
        </div>
      </Modal>
    </Layout>
  );
};

export default LinksManager; 